if (!customElements.get('vulgrco-hero')) {
  class VulgrcoHero extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.window = this.querySelector('.vh-window');
      this.track = this.querySelector('.vh-track');
      this.slides = [...this.querySelectorAll('.vh-slide')];
      this.dots = [...this.querySelectorAll('.vh-dot')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      this.index = 0;
      this.target = 0;
      this.request = 0;
      this.images = new Map();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        if (event.timeStamp < this.suppressClickUntil && this.window.contains(event.target)) {
          this.suppressClickUntil = 0;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const control = event.target.closest('[data-vh-go],[data-vh-step]');
        if (!control || !this.contains(control)) return;
        event.preventDefault();
        this.go(control.hasAttribute('data-vh-go') ? Number(control.dataset.vhGo) : this.target + Number(control.dataset.vhStep));
      }, { ...options, capture: true });
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        const keys = { ArrowLeft: this.target - 1, ArrowRight: this.target + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.go(keys[event.key]);
      }, options);
      this.window.addEventListener('pointerdown', event => this.pointerDown(event), options);
      this.window.addEventListener('pointermove', event => this.pointerMove(event), options);
      this.window.addEventListener('pointerup', event => this.pointerEnd(event), options);
      this.window.addEventListener('pointercancel', event => this.pointerEnd(event, true), options);
      this.window.addEventListener('lostpointercapture', event => {
        // Touch starts with implicit capture on a child. Its loss bubbles here
        // when we capture the swipe; only our own capture loss ends the drag.
        if (event.target === this.window) this.pointerEnd(event, true);
      }, options);
      this.window.addEventListener('dragstart', event => event.preventDefault(), options);
      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('.vh-slide');
        if (slide && this.contains(slide)) this.go(this.slides.indexOf(slide), false);
      }, options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.window.clientWidth;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.cancelMotion();
        this.pointer = null;
        this.setOffset(-this.index * this.step());
        this.warm(this.index);
      });
      this.resizeObserver.observe(this.window);
      this.update();
      this.warm(0);
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.request++;
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.cancelMotion();
      cancelAnimationFrame(this.frame);
      this.pointer = null;
    }

    step() {
      return this.slides[0].getBoundingClientRect().width + 40;
    }

    offset() {
      const transform = getComputedStyle(this.track).transform;
      return transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41;
    }

    setOffset(value) {
      this.track.style.transform = `translate3d(${value}px,0,0)`;
    }

    cancelMotion() {
      this.motion?.cancel();
      this.motion = null;
      delete this.track?.dataset.moving;
    }

    loadImage(url) {
      if (!url) return Promise.resolve();
      if (!this.images.has(url)) {
        const image = new Image();
        image.decoding = 'async';
        const loaded = new Promise(resolve => {
          image.onload = resolve;
          image.onerror = resolve;
        });
        image.src = url;
        this.images.set(url, loaded.then(() => image.decode().catch(() => {})));
      }
      return this.images.get(url);
    }

    prepare(index) {
      const slide = this.slides[index];
      if (!slide) return Promise.resolve();
      const background = slide.querySelector('.vh-backdrop');
      const narrow = matchMedia('(max-width:600px)').matches;
      const fullframe = slide.hasAttribute('data-fullframe') && matchMedia('(min-width:992px)').matches;
      const tasks = [];
      if (background) {
        for (const [key, property] of [['bgDesktop', '--vh-bg-desktop'], ['bgMobile', '--vh-bg-mobile']]) {
          if (background.dataset[key]) slide.style.setProperty(property, `url("${background.dataset[key]}")`);
        }
        if (!fullframe) tasks.push(this.loadImage(narrow ? background.dataset.bgMobile || background.dataset.bgDesktop : background.dataset.bgDesktop));
      }
      for (const image of slide.querySelectorAll('img')) {
        if (image.classList.contains('vh-fullframe') && !fullframe) continue;
        image.loading = 'eager';
        tasks.push(image.decode().catch(() => {}));
      }
      return Promise.all(tasks);
    }

    warm(index) {
      for (const n of [index - 1, index, index + 1]) this.prepare(n);
    }

    async go(index, animate = true) {
      if (!this.abort) return;
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      this.target = next;
      const request = ++this.request;
      const start = Math.min(this.index, next);
      const end = Math.max(this.index, next);
      await Promise.all(this.slides.slice(start, end + 1).map((slide, offset) => this.prepare(start + offset)));
      if (request !== this.request || !this.isConnected || !this.abort) return;
      const from = this.offset();
      this.cancelMotion();
      this.index = next;
      this.update();
      this.warm(next);
      const to = -next * this.step();
      this.setOffset(to);
      const timing = getComputedStyle(this).getPropertyValue('--vh-duration').trim();
      const duration = animate ? parseFloat(timing) * (timing.endsWith('ms') ? 1 : 1000) : 0;
      if (duration > 0 && Math.abs(to - from) > .1) {
        this.track.dataset.moving = 'true';
        const motion = this.track.animate([
          { transform: `translate3d(${from}px,0,0)` },
          { transform: `translate3d(${to}px,0,0)` }
        ], { duration, easing: 'cubic-bezier(.22,1,.36,1)' });
        this.motion = motion;
        motion.finished.then(() => {
          if (this.motion !== motion) return;
          this.motion = null;
          delete this.track.dataset.moving;
        }).catch(() => {});
      }
    }

    update() {
      this.slides.forEach((slide, index) => {
        const current = index === this.index;
        slide.setAttribute('aria-current', String(current));
        slide.setAttribute('aria-hidden', String(!current));
        slide.querySelectorAll('a,button').forEach(link => link.tabIndex = current ? 0 : -1);
      });
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      this.querySelector('.vh-prev').disabled = this.index === 0;
      this.querySelector('.vh-next').disabled = this.index === this.slides.length - 1;
    }

    pointerDown(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.suppressClickUntil = 0;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp, base: this.offset(), delta: 0, dragging: false };
    }

    pointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
          this.pointer = null;
          return;
        }
        if (Math.abs(dx) < 8) return;
        pointer.dragging = true;
        pointer.base = this.offset();
        this.request++;
        this.cancelMotion();
        this.window.setPointerCapture(event.pointerId);
      }
      pointer.delta = dx;
      const minimum = -(this.slides.length - 1) * this.step();
      let offset = pointer.base + dx;
      if (offset > 0) offset *= .35;
      if (offset < minimum) offset = minimum + (offset - minimum) * .35;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.setOffset(offset));
    }

    pointerEnd(event, cancelled = false) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      this.pointer = null;
      if (!pointer.dragging) return;
      cancelAnimationFrame(this.frame);
      if (this.window.hasPointerCapture(event.pointerId)) this.window.releasePointerCapture(event.pointerId);
      this.suppressClickUntil = event.timeStamp + 500;
      const distance = pointer.delta;
      const velocity = Math.abs(distance) / Math.max(1, event.timeStamp - pointer.time);
      const moved = !cancelled && (Math.abs(distance) > this.window.clientWidth / 10 || velocity > .5);
      const change = moved ? Math.max(1, Math.round(Math.abs(distance) / this.step())) * -Math.sign(distance) : 0;
      this.go(this.index + change);
    }
  }
  customElements.define('vulgrco-hero', VulgrcoHero);
}
